import htmx from "htmx.org";
import { Centrifuge } from "centrifuge";
import Handlebars from "handlebars";

// Define the extension name as a constant
const EXTENSION_NAME = "push-session";

/** @type {import("htmx.org").HtmxInternalApi} */
var api;

/**
 * HTMX Push Session Extension
 *
 * This extension enables real-time updates via Centrifuge websockets
 * with template rendering capabilities.
 */
htmx.defineExtension(EXTENSION_NAME, {
  // Initialize the extension
  init: function (api) {
    // This is called once when the extension is registered
    // console.log(`${EXTENSION_NAME} extension initialized`);
  },

  // Handle HTMX events if needed
  onEvent: function (name: string, evt: Event | CustomEvent<any>): boolean {
    var parent = evt.target || (evt as CustomEvent<any>).detail?.elt;

    switch (name) {
      // Try to close the socket when elements are removed
      case "htmx:beforeCleanupElement":
        // var internalData = api.getInternalData(parent);

        // // TODO: close centrifugo connection
        // if (internalData.webSocket) {
        //   internalData.webSocket.close();
        // }
        return false;

      // Try to create websockets when elements are processed
      case "htmx:beforeProcessNode":
        if (
          parent instanceof HTMLElement &&
          parent.getAttribute("hx-ext") === EXTENSION_NAME
        ) {
          initPushSession(parent);
        }
    }
    return true;
  },
});

// Actual implementation function
function initPushSession(elt: HTMLElement) {
  // Read configuration from data attributes (with defaults)
  const websocketUrl =
    elt.getAttribute("push-ws-url") ||
    "wss://realtime.dostow.com/connection/websocket";
  const sessionEndpoint =
    elt.getAttribute("push-session-endpoint") || "/api/push-sessions/new";
  const subscriptionEndpoint =
    elt.getAttribute("push-subscription-endpoint") || "/api/push-sessions/";
  const templateSelector =
    elt.getAttribute("push-template-selector") || "#" + elt.id + "-template";
  const targetSelector =
    elt.getAttribute("push-target-selector") || "#" + elt.id + "-results";
  const templateEngine = elt.getAttribute("template-engine") || "handlebars";
  const pushSessionSelector =
    elt.getAttribute("push-session-selector") || "#pushSession";
  const debug = elt.getAttribute("push-data-debug") === "true";
  /**
   * Fetch a new push session.
   * Expects session payload from the element specified by pushSessionSelector.
   */
  async function fetchPushSession() {
    const pushSessionElement = document.querySelector(pushSessionSelector);
    const response = await fetch(sessionEndpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: pushSessionElement?.textContent,
    });
    if (!response.ok) {
      throw new Error(`Failed to fetch push session: ${response.status}`);
    }
    return await response.json();
  }

  /**
   * Refresh the subscription token for a given session ID.
   * Returns the token from the first channel.
   */
  async function refreshSubscriptionToken(sessionId: string) {
    const pushSessionElement = document.querySelector(pushSessionSelector);
    const response = await fetch(subscriptionEndpoint + sessionId, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: pushSessionElement?.textContent,
    });
    if (!response.ok) {
      if (response.status === 403) {
        throw new Error("Unauthorized: token refresh denied");
      }
      throw new Error(`Unexpected response status: ${response.status}`);
    }
    const data = await response.json();
    if (!data.channels || data.channels.length === 0) {
      throw new Error("No channels returned from token refresh");
    }
    return data.channels[0].token;
  }

  /**
   * Render data using the specified template and engine
   */
  function renderTemplate(data: any, template: string | null, engine: string) {
    if (!template) {
      console.error("Template not found");
      return null;
    }
    switch (engine) {
      case "handlebars":
        const templateFn = Handlebars.compile(template);
        return templateFn(data);
      default:
        console.error(`Unsupported template engine: ${engine}`);
        return null;
    }
  }

  /**
   * Handle push message by rendering and updating the DOM
   */
  function handlePushMessage(data: { ranked_results: [] }) {
    if (!templateSelector) {
      console.error("No template selector specified");
      return;
    }

    const templateElement = document.querySelector(templateSelector);
    if (!templateElement) {
      console.error(`Template element not found: ${templateSelector}`);
      return;
    }
    const templateContent =
      templateElement.innerHTML || templateElement.textContent;

    const targetElement = document.querySelector(targetSelector);
    if (!targetElement) {
      console.error(`Target element not found: ${targetSelector}`);
      return;
    }

    // Create a document fragment for efficient DOM manipulation
    const fragment = document.createDocumentFragment();

    if (data.ranked_results && data.ranked_results.length > 0) {
      data.ranked_results.forEach((result) => {
        const renderedContent = renderTemplate(
          result,
          templateContent,
          templateEngine,
        );
        if (renderedContent) {
          const tempContainer = document.createElement("div");
          tempContainer.innerHTML = renderedContent;

          while (tempContainer.firstChild) {
            fragment.appendChild(tempContainer.firstChild);
          }
        }
      });

      // Swap the content properly using HTMX without clearing everything
      targetElement.replaceChildren(fragment);

      // Ensure HTMX processes the new content
      htmx.process(targetElement);
    }
  }

  /**
   * Set up the Centrifuge client, subscribe to the channel,
   * and handle received messages with the template.
   */
  async function setupPushSession() {
    try {
      const sessionData = await fetchPushSession();
      if (debug) {
        console.log("Push session response:", sessionData);
      }
      const { id: sessionId, channels, data: connectionData } = sessionData;
      if (!channels || channels.length === 0) return;

      const client = new Centrifuge(websocketUrl, {
        token: connectionData.token,
        debug: debug,
        getToken: async (ctx) => {
          const newSession = await fetchPushSession();
          if (debug) {
            console.log("Token refresh response:", newSession);
          }
          return newSession.data.token;
        },
      });

      const channelInfo = channels[0];
      const sub = client.newSubscription(channelInfo.channel, {
        token: channelInfo.token,
        getToken: async (ctx) => {
          const token = await refreshSubscriptionToken(sessionId);
          if (debug) {
            console.log("Subscription token refreshed:", token);
          }
          return token;
        },
      });

      // Handle publication by rendering template
      sub.on("publication", function (ctx) {
        // Dispatch custom event
        const pushEvent = new CustomEvent("pushMessage", {
          detail: ctx.data,
        });
        elt.dispatchEvent(pushEvent);

        // Render the template with the received data
        handlePushMessage(ctx.data);
      });

      // Start subscription and connect the client
      sub.subscribe();
      client.connect();
    } catch (error) {
      console.error("Push session setup error:", error);
    }
  }

  // Initialize Push Session setup
  setupPushSession();
}

// Export the extension name for importing in other files
export { EXTENSION_NAME };
